import React, { useMemo } from 'react';
import { MenuItem, SalvButton, SalvLabel, SalvMenuButton, SalvTable, Size } from '@salvapp/ui-components';
import { createColumnHelper } from '@tanstack/react-table';
import {
  assignParameterValues,
  getScenarioVersionStatus,
  scenarioVersionStatusColorVariant,
} from '../../common/versioning/helper';
import { CAN_DELETE_SCENARIO, CAN_MODIFY_SCENARIO } from '../../auth/permissions';
import Has from '../../auth/Has';
import FormattedDateTime from '../../common/datetime/FormattedDateTime';
import {
  InternalApiReviewRequest,
  InternalApiReviewRequestEntityType,
  InternalApiScenarioEntityType,
  InternalApiScenarioVersionBasic,
  InternalApiScenarioVersionStatus,
} from '../../api-internal';
import { useReviewRequests } from '../../review-requests/reviewRequestActions';

type VersionsV2Props = {
  versions: InternalApiScenarioVersionBasic[];
  entityType: InternalApiScenarioEntityType;
  isLibraryScenario: boolean;
  onDeleteVersion: (id: string) => void;
  onEditVersion: (id: string) => void;
  onDuplicateAsDraft: (id: string) => void;
  onTestVersion: (id: string) => void;
};

enum VersionAction {
  EDIT = 'edit',
  TEST = 'test',
  DUPLICATE = 'duplicate',
  DELETE = 'delete',
}

const inReview = (reviewRequests: InternalApiReviewRequest[] | undefined, version: InternalApiScenarioVersionBasic) =>
  !!reviewRequests?.filter((request) => request.entityId === version.id).length;

export const ScenarioVersionsTable = ({
  versions,
  onEditVersion,
  onDuplicateAsDraft,
  onDeleteVersion,
  onTestVersion,
  isLibraryScenario,
}: VersionsV2Props): React.ReactElement | null => {
  const { data: reviewRequests } = useReviewRequests(
    versions.map((version) => version.id),
    InternalApiReviewRequestEntityType.SCENARIO_VERSION
  );
  const onVersionAction = (version: InternalApiScenarioVersionBasic, action: VersionAction) => {
    switch (action) {
      case VersionAction.EDIT:
        return onEditVersion(version.id);
      case VersionAction.TEST:
        return onTestVersion(version.id);
      case VersionAction.DUPLICATE:
        return onDuplicateAsDraft(version.id);
      case VersionAction.DELETE:
        return onDeleteVersion(version.id);
    }
  };

  const columnHelper = createColumnHelper<InternalApiScenarioVersionBasic>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('versionId', {
        header: 'Version',
        cell: ({ getValue }) => getValue(),
      }),
      columnHelper.accessor('status', {
        header: 'Status',
        cell: ({ row }) => (
          <SalvLabel variant={scenarioVersionStatusColorVariant(row.original.status)}>
            {getScenarioVersionStatus(row.original.status, inReview(reviewRequests, row.original))}
          </SalvLabel>
        ),
      }),
      columnHelper.accessor('logic', {
        header: 'Logic',
        cell: ({ row }) => {
          return row.original.logic && row.original.parameters
            ? assignParameterValues(row.original.logic, row.original.parameters)
            : '-';
        },
      }),
      columnHelper.accessor('note', { header: 'Note' }),

      columnHelper.accessor('updatedTime', {
        header: 'Last updated',
        cell: ({ getValue }) => <FormattedDateTime date={getValue()} showTime />,
      }),
      columnHelper.accessor('updatedBy', { header: 'Modifier', cell: ({ getValue }) => getValue()?.name }),

      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        size: 0,
        cell: ({ row }) => {
          return (
            <Has anyPermissions={[CAN_MODIFY_SCENARIO, CAN_DELETE_SCENARIO]}>
              {(can) => {
                return can && !isLibraryScenario ? (
                  <SalvMenuButton
                    ariaLabel='version-edit-dropdown'
                    onAction={(action) => onVersionAction(row.original, action[0] as VersionAction)}
                    placement='bottom right'
                  >
                    <MenuItem
                      title={row.original.status === InternalApiScenarioVersionStatus.DRAFT ? 'Edit' : 'View'}
                      value={VersionAction.EDIT}
                    />
                    {row.original.status !== InternalApiScenarioVersionStatus.HISTORIC && (
                      <MenuItem title='Test' value={VersionAction.TEST} />
                    )}
                    <MenuItem title='Duplicate as draft' value={VersionAction.DUPLICATE} />
                    {row.original.status === InternalApiScenarioVersionStatus.DRAFT &&
                      !inReview(reviewRequests, row.original) && (
                        <MenuItem title='Delete' value={VersionAction.DELETE} />
                      )}
                  </SalvMenuButton>
                ) : (
                  <SalvButton
                    variant='muted'
                    size={Size.EXTRA_SMALL}
                    onClick={() => onEditVersion(row.original.id)}
                    label='View'
                  />
                );
              }}
            </Has>
          );
        },
      }),
    ],
    [reviewRequests]
  );

  return (
    <SalvTable
      data={versions}
      columns={columns}
      rowClick={(row) => onEditVersion(row.original.id)}
      className='m-t-16'
      emptyMessage='No scenario versions found.'
    />
  );
};

export default ScenarioVersionsTable;
